import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router';
import { SortOrder } from 'types/interfaces/sort';

interface SortState {
  sort: string | undefined;
  direction: SortOrder | undefined;
}

function parseSortCompound(value: string): SortState | null {
  const lastDash = value.lastIndexOf('-');
  if (lastDash <= 0) return null;
  const sort = value.substring(0, lastDash);
  const direction = value.substring(lastDash + 1);
  if (direction !== 'asc' && direction !== 'desc') return null;
  return { sort, direction };
}

/**
 * Persists sort state to URL query params and localStorage.
 * Reads from URL param `?sort=field-direction`, falls back to localStorage, then default.
 * Syncs changes to both URL (replace) and localStorage.
 */
export function useSortPreference(
  storageKey: string,
  defaultSort: string,
  defaultDirection: SortOrder
) {
  const [searchParams, setSearchParams] = useSearchParams();

  const defaultCompound = `${defaultSort}-${defaultDirection}`;

  const getInitialSort = (): SortState => {
    const urlSort = searchParams.get('sort');
    if (urlSort) {
      const parsed = parseSortCompound(urlSort);
      if (parsed) return parsed;
    }

    const saved = localStorage.getItem(storageKey);
    if (saved) {
      const parsed = parseSortCompound(saved);
      if (parsed) return parsed;
    }

    return { sort: defaultSort, direction: defaultDirection };
  };

  const [orderBy, setOrderByState] = useState<SortState>(getInitialSort);

  const compound = `${orderBy.sort}-${orderBy.direction}`;

  // Two traps here, both of which re-added a one-shot param (?import=, ?openBulkManage=)
  // that a child had just stripped on mount:
  // 1. react-router recreates setSearchParams on every URL change and a functional
  //    update reads the searchParams captured when THAT setter was created, so depending
  //    on the setter re-ran this effect after the strip and replayed the stale search.
  // 2. Even on a single run, a parent's mount effect fires after its child's, so the
  //    mount-time `prev` still carries the param the child removed a moment earlier.
  // So: keep the latest setter in a ref, build the next params from the LIVE URL rather
  // than `prev`, and skip the write entirely when the URL already matches.
  // Assumption: window.location.search is the router's search, i.e. a BrowserRouter with
  // no basename (true for the app). Under a MemoryRouter (tests) the URL search is empty,
  // so this effect computes from '' and can overwrite the router's own params; render list
  // surfaces under BrowserRouter in tests, or expect sort params to vanish.
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;

  // Sync to URL and localStorage when sort changes
  useEffect(() => {
    // Always persist to localStorage
    localStorage.setItem(storageKey, compound);

    // Update URL params
    const current = new URLSearchParams(window.location.search);
    const newParams = new URLSearchParams(current);
    if (compound === defaultCompound) {
      newParams.delete('sort');
    } else {
      newParams.set('sort', compound);
    }
    if (newParams.toString() === current.toString()) return;
    setSearchParamsRef.current(newParams, { replace: true });
  }, [compound, defaultCompound, storageKey]);

  const setOrderBy: React.Dispatch<React.SetStateAction<SortState>> = useCallback(
    (value) => {
      setOrderByState(value);
    },
    []
  );

  return { orderBy, setOrderBy, compound };
}
